import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, Handshake, CheckCircle, XCircle, Shield, AlertTriangle, Smartphone } from 'lucide-react';

const AffiliateTerms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 md:p-8 bg-gradient-to-br from-orange-50 to-amber-100">
      <div className="max-w-4xl mx-auto">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6 text-lg font-semibold hover:scale-105 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <Card className="p-8 md:p-12 bg-white shadow-xl" style={{ borderRadius: '24px' }}>
          <div className="flex items-center gap-4 mb-8">
            <div className="p-4 bg-orange-100 rounded-2xl">
              <Handshake className="w-10 h-10 text-orange-600" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Affiliate & Partner Terms of Service</h1>
              <p className="text-gray-500">Little Genius Hub & Affiliates</p>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-8">Last Updated: 1/1/26</p>

          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="mb-8 text-lg">
              These Affiliate Terms govern participation in the Little Genius Hub Affiliate Program.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-orange-500" />
              1. Eligibility
            </h2>
            <p className="mb-4">Affiliates must:</p>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>Be at least 18 years old</li>
              <li>Operate in compliance with all applicable laws</li>
              <li>Promote content in a family-friendly and ethical manner</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">2. Approved Promotion Methods</h2>
            <p className="mb-4">Affiliates may promote using:</p>
            <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4 rounded-r-lg">
              <ul className="list-disc pl-6 space-y-2 text-green-800">
                <li>Websites</li>
                <li>Social media</li>
                <li>Email marketing (opt-in only)</li>
              </ul>
            </div>
            
            <p className="mb-4">Prohibited methods include:</p>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
              <ul className="list-disc pl-6 space-y-2 text-red-800">
                <li>Spam</li>
                <li>False or misleading claims</li>
                <li>Misrepresentation of educational benefits</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-orange-500" />
              3. Brand Usage
            </h2>
            <p className="mb-6">
              Affiliates may use approved branding materials only. Modification of logos or trademarks is not permitted without written approval.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <XCircle className="w-6 h-6 text-red-500" />
              4. Prohibited Content
            </h2>
            <p className="mb-4">Affiliates may NOT associate Little Genius Hub with:</p>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
              <ul className="list-disc pl-6 space-y-2 text-red-800">
                <li>Adult content</li>
                <li>Hate speech</li>
                <li>Violence</li>
                <li>Misleading claims</li>
              </ul>
            </div>

            {/* Platform Compliance Requirement */}
            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-orange-500" />
              5. Platform Compliance Requirement
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
              <p className="font-semibold text-blue-800 mb-3">App Store & Play Store Compliance</p>
              <p className="text-blue-800 mb-2">All affiliates must:</p>
              <ul className="list-disc pl-6 space-y-2 text-blue-800">
                <li>Follow <strong>Apple App Store Guidelines</strong> when promoting the iOS application</li>
                <li>Follow <strong>Google Play Store Policies</strong> when promoting the Android application</li>
                <li><strong>Not target children directly</strong> with marketing or promotional materials</li>
                <li>Direct all promotional efforts toward parents, guardians, and educators</li>
                <li>Comply with COPPA and other child privacy regulations in all marketing activities</li>
              </ul>
            </div>

            {/* No Misrepresentation Clause */}
            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              6. No Misrepresentation Clause
            </h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
              <p className="font-semibold text-red-800 mb-3">Affiliates May NOT Claim:</p>
              <ul className="list-disc pl-6 space-y-2 text-red-800">
                <li><strong>Guaranteed learning results</strong> – Educational outcomes vary by child and usage</li>
                <li><strong>Official Apple endorsement</strong> – Little Genius Hub is not endorsed, sponsored, or affiliated with Apple Inc.</li>
                <li><strong>Official Google endorsement</strong> – Little Genius Hub is not endorsed, sponsored, or affiliated with Google LLC</li>
                <li><strong>Medical or therapeutic benefits</strong> – The app is for educational entertainment only</li>
                <li><strong>Any partnership not explicitly authorized</strong> in writing by Little Genius Hub</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">7. Termination</h2>
            <p className="mb-6">
              We reserve the right to terminate partnerships at any time for violations or reputational risk.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">8. Limitation of Liability</h2>
            <p className="mb-6">
              Little Genius Hub is not responsible for indirect or consequential damages arising from affiliate participation.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">9. Governing Law</h2>
            <p className="mb-6">
              These terms are governed by the laws of the United States.
            </p>

            <h2 className="text-2xl font-bold text-gray-800 mt-8 mb-4">10. Contact</h2>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p><strong>Email:</strong> <a href="mailto:support@littlegeniushub.com" className="text-blue-600 hover:underline">support@littlegeniushub.com</a></p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AffiliateTerms;
